/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import classNames from 'classnames';
import PropTypes from "prop-types";
import React from 'react';
import { EuiScreenReaderOnly } from '../accessibility';
import { keysOf } from '../common';
import { EuiIcon } from '../icon';
import { useI18nCompleteStep, useI18nDisabledStep, useI18nErrorsStep, useI18nIncompleteStep, useI18nStep, useI18nWarningStep, useI18nLoadingStep, useI18nCurrentStep } from './step_strings';
import { EuiLoadingSpinner } from '../loading';
var statusToClassNameMap = {
  incomplete: 'euiStepNumber--incomplete',
  disabled: 'euiStepNumber--disabled',
  loading: 'euiStepNumber--loading',
  warning: 'euiStepNumber--warning',
  danger: 'euiStepNumber--danger',
  complete: 'euiStepNumber--complete',
  current: null // Current displays the same as the default (undefined)

};
export var STATUS = keysOf(statusToClassNameMap);
export var EuiStepNumber = ({
  className,
  status,
  number,
  titleSize,
  ...rest
}) => {
  const stepAriaLabel = useI18nStep({
    number
  });
  const completeAriaLabel = useI18nCompleteStep({
    number
  });
  const warningAriaLabel = useI18nWarningStep({
    number
  });
  const errorsAriaLabel = useI18nErrorsStep({
    number
  });
  const incompleteAriaLabel = useI18nIncompleteStep({
    number
  });
  const disabledAriaLabel = useI18nDisabledStep({
    number
  });
  const loadingAriaLabel = useI18nLoadingStep({
    number
  });
  const currentAriaLabel = useI18nCurrentStep({
    number
  });
  const classes = classNames('euiStepNumber', status ? statusToClassNameMap[status] : undefined, className);
  const iconSize = titleSize === 'xs' ? 's' : 'm';
  let screenReaderText = stepAriaLabel;
  if (status === 'incomplete') screenReaderText = incompleteAriaLabel;else if (status === 'disabled') screenReaderText = disabledAriaLabel;else if (status === 'loading') screenReaderText = loadingAriaLabel;else if (status === 'current') screenReaderText = currentAriaLabel;
  let numberOrIcon = <>
      <EuiScreenReaderOnly>
        <span>{screenReaderText}</span>
      </EuiScreenReaderOnly>
      <span className="euiStepNumber__number" aria-hidden="true">
        {number}
      </span>
    </>;

  if (status === 'complete') {
    numberOrIcon = <EuiIcon type="check" className="euiStepNumber__icon" size={iconSize} aria-label={completeAriaLabel} />;
  } else if (status === 'warning') {
    numberOrIcon = <EuiIcon type="alert" className="euiStepNumber__icon" size={iconSize} aria-label={warningAriaLabel} />;
  } else if (status === 'danger') {
    numberOrIcon = <EuiIcon type="cross" className="euiStepNumber__icon" size={iconSize} aria-label={errorsAriaLabel} />;
  } else if (status === 'loading') {
    numberOrIcon = <>
        <EuiScreenReaderOnly>
          <span>{screenReaderText}</span>
        </EuiScreenReaderOnly>
        <EuiLoadingSpinner className="euiStepNumber__loader" size={iconSize === 's' ? 'l' : 'xl'} />
      </>;
  }

  return <span className={classes} {...rest}>
      {numberOrIcon}
    </span>;
};
EuiStepNumber.propTypes = {
  /**
     * May replace the number provided in props.number with alternate styling
     */
  status: PropTypes.any,
  number: PropTypes.number,

  /**
     * Title sizing equivalent to EuiTitle, but only `m`, `s` and `xs`. Defaults to `s`
     */
  titleSize: PropTypes.any,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};

try {
  EuiStepNumber.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/steps/step_number.tsx',
    description: '',
    displayName: 'EuiStepNumber',
    methods: [],
    props: {
      status: {
        defaultValue: null,
        description: 'May replace the number provided in props.number with alternate styling',
        name: 'status',
        parent: {
          fileName: 'app/src/components/steps/step_number.tsx',
          name: 'EuiStepNumberProps'
        },
        declarations: [{
          fileName: 'app/src/components/steps/step_number.tsx',
          name: 'EuiStepNumberProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"danger" | "warning" | "disabled" | "loading" | "incomplete" | "complete" | "current"',
          value: [{
            value: '"danger"'
          }, {
            value: '"warning"'
          }, {
            value: '"disabled"'
          }, {
            value: '"loading"'
          }, {
            value: '"incomplete"'
          }, {
            value: '"complete"'
          }, {
            value: '"current"'
          }]
        }
      },
      number: {
        defaultValue: null,
        description: '',
        name: 'number',
        parent: {
          fileName: 'app/src/components/steps/step_number.tsx',
          name: 'EuiStepNumberProps'
        },
        declarations: [{
          fileName: 'app/src/components/steps/step_number.tsx',
          name: 'EuiStepNumberProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      titleSize: {
        defaultValue: null,
        description: 'Title sizing equivalent to EuiTitle, but only `m`, `s` and `xs`. Defaults to `s`',
        name: 'titleSize',
        parent: {
          fileName: 'app/src/components/steps/step_number.tsx',
          name: 'EuiStepNumberProps'
        },
        declarations: [{
          fileName: 'app/src/components/steps/step_number.tsx',
          name: 'EuiStepNumberProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"xs" | "s" | "m"',
          value: [{
            value: '"xs"'
          }, {
            value: '"s"'
          }, {
            value: '"m"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['EuiStepNumberProps', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}